import { useEffect, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import {
  appendKeepImageIds,
  getContentId,
  getInitialImageList,
  getToday,
  makePreviewImage,
} from "./contentFormUtils";

function EventForm({
  mode = "write",
  initialData = {},
  onCancel,
  onSaveComplete,
}) {
  const isEdit = mode === "edit";

  const [eventName, setEventName] = useState(
    initialData?.eventName || initialData?.title || ""
  );
  const [title, setTitle] = useState(initialData?.title || "");
  const [startDate, setStartDate] = useState(
    initialData?.startDate || getToday()
  );
  const [endDate, setEndDate] = useState(initialData?.endDate || getToday());
  const [introduction, setIntroduction] = useState(
    initialData?.introduction || ""
  );
  const [overview, setOverview] = useState(initialData?.overview || "");
  const [detailProgram, setDetailProgram] = useState(
    initialData?.detailProgram || ""
  );

  const [mainImgFile, setMainImgFile] = useState(null);
  const [mainImgName, setMainImgName] = useState(
    initialData?.mainImg ? "기존 대표 이미지" : ""
  );
  const [images, setImages] = useState(() => getInitialImageList(initialData));
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const hasExistingMainImg = Boolean(isEdit && initialData?.mainImg);

  useEffect(() => {
    if (!isEdit) return;

    setEventName(initialData?.eventName || initialData?.title || "");
    setTitle(initialData?.title || "");
    setStartDate(initialData?.startDate || getToday());
    setEndDate(initialData?.endDate || getToday());
    setIntroduction(initialData?.introduction || "");
    setOverview(initialData?.overview || "");
    setDetailProgram(initialData?.detailProgram || "");
    setMainImgFile(null);
    setMainImgName(initialData?.mainImg ? "기존 대표 이미지" : "");
    setImages(getInitialImageList(initialData));
    setImageFiles([]);
  }, [isEdit, initialData]);

  const handleMainImageChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setMainImgFile(selectedFile);
    setMainImgName(selectedFile.name);

    const previewImage = makePreviewImage(selectedFile, true);

    setImages((prev) => {
      const withoutOldMain = prev.filter((image) => !image.isMain);
      return [previewImage, ...withoutOldMain].slice(0, 5);
    });

    e.target.value = "";
  };

  const handleRemoveMainImage = () => {
    setMainImgFile(null);

    if (initialData?.mainImg) {
      setMainImgName("기존 대표 이미지");
      setImages(getInitialImageList(initialData));
      return;
    }

    setMainImgName("");
    setImages((prev) => prev.filter((image) => !image.isMain));
  };

  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const remainCount = 5 - images.length;
    const limitedFiles = selectedFiles.slice(0, Math.max(remainCount, 0));

    if (!limitedFiles.length) {
      alert("이미지는 최대 5개까지 등록할 수 있습니다.");
      return;
    }

    const newImages = limitedFiles.map((selectedFile) =>
      makePreviewImage(selectedFile, false)
    );

    setImages((prev) => [...prev, ...newImages].slice(0, 5));
    setImageFiles((prev) => [...prev, ...limitedFiles].slice(0, 5));

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const target = images[index];

    if (target?.isExisting && target?.isMain) {
      alert("기존 대표 이미지는 새 대표 이미지를 업로드하면 교체됩니다.");
      return;
    }

    const newImageIndex = images
      .slice(0, index)
      .filter((image) => !image.isExisting && !image.isMain).length;

    setImages((prev) => prev.filter((_, i) => i !== index));

    if (!target?.isMain && !target?.isExisting) {
      setImageFiles((prev) => prev.filter((_, i) => i !== newImageIndex));
    }

    if (target?.isMain) {
      setMainImgFile(null);
      setMainImgName(initialData?.mainImg ? "기존 대표 이미지" : "");
    }
  };

  const appendEventImages = (formData) => {
    const shouldUseFirstImageAsMain =
      !mainImgFile && !hasExistingMainImg && imageFiles.length > 0;

    const mainImageFile =
      mainImgFile || (shouldUseFirstImageAsMain ? imageFiles[0] : null);

    if (mainImageFile) {
      formData.append("mainImgFile", mainImageFile);
    }

    const detailImageFiles = shouldUseFirstImageAsMain
      ? imageFiles.slice(1)
      : imageFiles;

    detailImageFiles.forEach((imageFile) => {
      formData.append("imageFiles", imageFile);
    });
  };

  const handleSubmit = async () => {
    if (!eventName.trim()) {
      alert("행사명을 입력하세요.");
      return;
    }

    if (!title.trim()) {
      alert("제목을 입력하세요.");
      return;
    }

    if (!startDate || !endDate) {
      alert("행사 기간을 선택하세요.");
      return;
    }

    if (!introduction.trim()) {
      alert("행사 소개를 입력하세요.");
      return;
    }

    if (!overview.trim()) {
      alert("행사 개요를 입력하세요.");
      return;
    }

    if (!detailProgram.trim()) {
      alert("상세 프로그램을 입력하세요.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("eventName", eventName);
      formData.append("title", title);
      formData.append("startDate", startDate);
      formData.append("endDate", endDate);
      formData.append("introduction", introduction);
      formData.append("overview", overview);
      formData.append("detailProgram", detailProgram);

      if (isEdit) {
        appendKeepImageIds(formData, images);
      }

      appendEventImages(formData);

      if (isEdit) {
        await apiClient(`/api/activity/event/${getContentId(initialData)}`, {
          method: "PUT",
          body: formData,
        });
      } else {
        await apiClient("/api/activity/event", {
          method: "POST",
          body: formData,
        });
      }

      alert(isEdit ? "수정되었습니다." : "등록되었습니다.");

      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      console.error("행사 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-form-page event-form-page">
      <h2 className="content-form-title">
        {isEdit ? "행사 수정" : "새 행사 작성"}
      </h2>

      <div className="content-form event-form">
        <div className="content-form-row">
          <label className="content-label">행사명</label>

          <input
            className="content-input"
            type="text"
            placeholder="행사명을 입력해 주세요."
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
          />
        </div>

        <div className="content-form-row">
          <label className="content-label">제목</label>

          <input
            className="content-input"
            type="text"
            placeholder="제목을 입력해 주세요."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="content-form-row">
          <label className="content-label">행사 기간</label>

          <div className="content-date-range">
            <input
              className="content-input content-date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <span>~</span>

            <input
              className="content-input content-date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="content-form-row">
          <label className="content-label">대표 이미지</label>

          <div className="content-file-line">
            <input
              className="content-input"
              type="text"
              placeholder="대표 이미지를 업로드해 주세요."
              value={mainImgName}
              readOnly
            />

            {mainImgName && (
              <button
                type="button"
                className="content-small-remove"
                onClick={handleRemoveMainImage}
              >
                ×
              </button>
            )}

            <label className="content-file-select-btn">
              파일 선택
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleMainImageChange}
              />
            </label>
          </div>
        </div>

        <ImageUploadRow
          images={images}
          onImageChange={handleImageChange}
          onRemoveImage={handleRemoveImage}
        />

        <div className="content-form-row content-form-row-top">
          <label className="content-label">행사 소개</label>

          <textarea
            className="content-textarea event-textarea"
            placeholder="행사 소개를 작성해 주세요.&#10;(최대 300자까지 입력 가능합니다.)"
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
          />
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">행사 개요</label>

          <textarea
            className="content-textarea event-textarea"
            placeholder="행사 개요, 주요 내용 등을 작성해 주세요.&#10;(최대 300자까지 입력 가능합니다.)"
            value={overview}
            onChange={(e) => setOverview(e.target.value)}
          />
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">상세 프로그램</label>

          <textarea
            className="content-textarea event-textarea"
            placeholder="상세 프로그램 일정을 작성해 주세요.&#10;(최대 300자까지 입력 가능합니다.)"
            value={detailProgram}
            onChange={(e) => setDetailProgram(e.target.value)}
          />
        </div>

        <FormFooter
          loading={loading}
          onCancel={onCancel}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}

function ImageUploadRow({ images, onImageChange, onRemoveImage }) {
  return (
    <div className="content-form-row content-form-row-top">
      <label className="content-label">이미지</label>

      <div className="content-image-box">
        {images.map((img, index) => (
          <div
            className="content-image-item"
            key={`${img.imageId || img.fileName || "image"}-${index}`}
          >
            {!img.isMain && (
              <button
                type="button"
                className="content-image-remove"
                onClick={() => onRemoveImage(index)}
              >
                ×
              </button>
            )}

            {img.preview || img.url ? (
              <img src={img.preview || img.url} alt="preview" />
            ) : (
              <span>{img.fileName || "기존 이미지"}</span>
            )}
          </div>
        ))}

        {images.length < 5 && (
          <label className="content-image-add">
            +
            <input
              type="file"
              accept="image/*"
              hidden
              multiple
              onChange={onImageChange}
            />
          </label>
        )}
      </div>
    </div>
  );
}

function FormFooter({ loading, onCancel, onSubmit }) {
  return (
    <div className="content-form-footer">
      <button
        type="button"
        className="content-cancel-btn"
        onClick={onCancel}
        disabled={loading}
      >
        취소
      </button>

      <button
        type="button"
        className="content-save-btn"
        onClick={onSubmit}
        disabled={loading}
      >
        {loading ? "저장 중..." : "저장"}
      </button>
    </div>
  );
}

export default EventForm;